// src/pages/Home/components/Navbar3/components/NavbarMenuPanel.jsx

import React from "react";

import {
  MENU_SECTIONS,
} from "../data/navbar3.data";

import {
  cx,
  formatBadgeCount,
  getMenuBadgeMeta,
  isPathActive,
} from "../utils/navbar3.utils";

function NavbarMenuItem({
  item,
  currentPath,
  hasAccess,
  badgeCount,
  onNavigate,
}) {
  const Icon = item.icon;

  const isActive =
    isPathActive(
      currentPath,
      item.path,
    );

  const badgeMeta =
    getMenuBadgeMeta(
      item.badge,
    );

  return (
    <li>
      <button
        type="button"
        disabled={!hasAccess}
        onClick={() =>
          onNavigate(
            item.path,
            item.roles,
          )
        }
        aria-current={
          isActive
            ? "page"
            : undefined
        }
        className={cx(
          [
            "group flex w-full",
            "items-center gap-2.5",
            "rounded-lg",
            "px-2.5 py-2",
            "text-left",
            "text-sm font-medium",
            "transition",
            "duration-200",
          ].join(" "),
          hasAccess
            ? [
                "text-slate-200",
                "hover:bg-white/10",
                "hover:text-white",
                "focus-visible:outline-none",
                "focus-visible:ring-2",
                "focus-visible:ring-white/40",
              ].join(" ")
            : [
                "cursor-not-allowed",
                "text-slate-600",
              ].join(" "),
          isActive &&
            hasAccess
            ? [
                "bg-white/10",
                "text-white",
                "ring-1",
                "ring-white/10",
              ].join(" ")
            : "",
        )}
      >
        <span
          className={cx(
            [
              "relative flex h-8 w-8",
              "shrink-0 items-center",
              "justify-center",
              "rounded-lg",
              "transition",
            ].join(" "),
            hasAccess
              ? isActive
                ? "bg-white/20 text-white"
                : "bg-white/8 text-slate-300 group-hover:bg-white/15 group-hover:text-white"
              : "bg-white/5 text-slate-600",
          )}
        >
          {Icon ? (
            <Icon className="text-sm" />
          ) : null}
        </span>

        <span
          className="
            min-w-0 flex-1
            leading-5
          "
        >
          {item.label}
        </span>

        {badgeCount > 0 ? (
          <span
            className={cx(
              [
                "inline-flex shrink-0",
                "items-center gap-1",
                "rounded-full",
                "px-2 py-0.5",
                "text-[10px]",
                "font-semibold",
                "shadow-sm",
              ].join(" "),
              badgeMeta.className,
            )}
          >
            {formatBadgeCount(
              badgeCount,
            )}

            <span className="hidden sm:inline">
              {badgeCount > 1
                ? badgeMeta.pluralLabel
                : badgeMeta.label}
            </span>
          </span>
        ) : null}
      </button>
    </li>
  );
}

function NavbarMenuSection({
  section,
  currentPath,
  hasAccessToItem,
  getItemBadgeCount,
  onNavigate,
}) {
  return (
    <section
      className="
        min-w-0
        rounded-2xl
        bg-white/[0.025]
        p-3
        ring-1
        ring-white/[0.04]
      "
    >
      <h2
        className={cx(
          "mb-2.5 border-b-2",
          section.borderClass,
        )}
      >
        <span
          className={cx(
            [
              "inline-flex",
              "rounded-t-md",
              "px-3 py-1.5",
              "text-xs",
              "font-semibold",
              "tracking-wide",
              "text-white",
              "sm:text-sm",
            ].join(" "),
            section.titleClass,
          )}
        >
          {section.title}
        </span>
      </h2>

      <ul className="space-y-1">
        {section.items.map(
          (item) => {
            const hasAccess =
              hasAccessToItem(
                item.roles,
              );

            const badgeCount =
              getItemBadgeCount(
                item.badge,
              );

            return (
              <NavbarMenuItem
                key={item.path}
                item={item}
                currentPath={
                  currentPath
                }
                hasAccess={
                  hasAccess
                }
                badgeCount={
                  badgeCount
                }
                onNavigate={
                  onNavigate
                }
              />
            );
          },
        )}
      </ul>
    </section>
  );
}

export default function NavbarMenuPanel({
  menuOpen,
  currentPath,
  hasAccessToItem,
  getItemBadgeCount,
  onNavigate,
}) {
  return (
    <div
      id="main-navigation-panel"
      aria-hidden={!menuOpen}
      className={cx(
        [
          "fixed inset-x-0 bottom-0",
          "top-[112px] z-40",
          "overflow-y-auto",
          "overscroll-contain",
          "bg-slate-950/95",
          "text-white",
          "backdrop-blur-xl",
          "transition-all",
          "duration-300",
          "ease-out",
          "md:top-[100px]",
        ].join(" "),
        menuOpen
          ? [
              "visible",
              "translate-y-0",
              "opacity-100",
            ].join(" ")
          : [
              "invisible",
              "-translate-y-3",
              "pointer-events-none",
              "opacity-0",
            ].join(" "),
      )}
    >
      <div
        className="
          mx-auto grid
          max-w-7xl
          grid-cols-1
          gap-4
          px-3
          pb-[calc(3.5rem+env(safe-area-inset-bottom))]
          pt-4
          sm:px-5
          sm:pt-5
          md:grid-cols-2
          md:gap-5
          md:px-6
          md:pb-8
          xl:grid-cols-3
        "
      >
        {MENU_SECTIONS.map(
          (section) => (
            <NavbarMenuSection
              key={section.id}
              section={section}
              currentPath={
                currentPath
              }
              hasAccessToItem={
                hasAccessToItem
              }
              getItemBadgeCount={
                getItemBadgeCount
              }
              onNavigate={
                onNavigate
              }
            />
          ),
        )}

        <div
          aria-hidden="true"
          className="h-4 md:hidden"
        />
      </div>
    </div>
  );
}